import type { TaxTypeDetail } from "./types/multiTax.types";

const CACHE_NAMESPACE = "multitax-tax-type-details";

// v2: the tree now spans every supported multi-tax country (US + CA), so v1
// entries (US-only) must not hydrate it.
const CACHE_PREFIX = `${CACHE_NAMESPACE}:v2`;

interface CacheKeyParts {
    accountId?: string | number | null;
    userId?: string | number | null;
    companyId?: string | number | null;
}

const cleanPart = (value: string | number | null | undefined, fallback: string): string =>
    value === null || value === undefined || value === "" ? fallback : String(value);

/**
 * Builds the session-storage cache key for the TaxType details tree.
 *
 * The key is scoped to (accountId, userId, companyId). The cached tree holds
 * every multi-tax country the company is eligible in (each row carries its own
 * `country`), so no country segment is needed in the key.
 */
export const buildTaxTypeDetailsCacheKey = ({
    accountId,
    userId,
    companyId,
}: CacheKeyParts): string | null => {
    if (companyId === null || companyId === undefined || companyId === "") return null;

    return [
        CACHE_PREFIX,
        cleanPart(accountId, "no-account"),
        cleanPart(userId, "no-user"),
        String(companyId),
    ].join(":");
};

const normalizeTaxTypeDetails = (details: TaxTypeDetail[]): TaxTypeDetail[] =>
    details.map(tt => ({
        taxTypeId: tt.taxTypeId,
        taxTypeIdSK: tt.taxTypeIdSK ?? null,
        taxSubTypeDetails: (tt.taxSubTypeDetails ?? []).map(st => ({
            taxSubTypeId: st.taxSubTypeId,
            taxSubTypeIdSK: st.taxSubTypeIdSK ?? null,
            taxTypeMappingId: st.taxTypeMappingId,
            jurisdictions: (st.jurisdictions ?? []).map(j => ({
                country: j.country,
                region: j.region,
                jurisTypeId: j.jurisTypeId,
                jurisdictionTypeId: j.jurisdictionTypeId || j.jurisTypeId,
                jurisCode: j.jurisCode,
                jurisName: j.jurisName,
                shortName: j.shortName ?? null,
            })),
        })),
    }));

export const readTaxTypeDetailsSessionCache = (key: string | null): TaxTypeDetail[] | null => {
    if (!key) return null;

    try {
        const raw = sessionStorage.getItem(key);
        if (!raw) return null;
        const parsed: unknown = JSON.parse(raw);
        return Array.isArray(parsed) ? (parsed as TaxTypeDetail[]) : null;
    } catch {
        return null;
    }
};

export const writeTaxTypeDetailsSessionCache = (
    key: string | null,
    details: TaxTypeDetail[]
): void => {
    if (!key) return;

    try {
        sessionStorage.setItem(key, JSON.stringify(normalizeTaxTypeDetails(details)));
    } catch {
        // Ignore quota/private-mode failures; Redux still holds data for the current page.
    }
};

export const clearTaxTypeDetailsSessionCache = (): void => {
    try {
        const keys: string[] = [];
        for (let i = 0; i < sessionStorage.length; i += 1) {
            const key = sessionStorage.key(i);
            // Every version, so entries from an older prefix are cleared too.
            if (key?.startsWith(`${CACHE_NAMESPACE}:`)) {
                keys.push(key);
            }
        }
        keys.forEach(key => sessionStorage.removeItem(key));
    } catch {
        // Ignore storage access failures during logout/session cleanup.
    }
};
